import { useEffect } from 'react'
import { Link, Outlet, ScrollRestoration, useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { useAuth } from '@/auth/AuthContext'

/**
 * Port of Views/Shared/_Layout.cshtml — the navbar, the footer and the
 * scroll/WOW behaviour from its inline <script>.
 *
 * Markup and class names are copied verbatim so the template CSS applies
 * unchanged; only `<a href="@Url.Action(...)">` becomes `<Link to>` for the
 * in-app routes.
 */
declare global {
  interface Window {
    WOW?: new (options?: Record<string, unknown>) => { init: () => void }
    layout_change?: (theme: string) => void
    layout_sidebar_change?: (value: string) => void
    change_box_container?: (value: string) => void
    layout_caption_change?: (value: string) => void
    layout_rtl_change?: (value: string) => void
    preset_change?: (preset: string) => void
    feather?: { replace: () => void }
  }
}

export function AppLayout(): JSX.Element {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  // The six theme calls at the bottom of _Layout.cshtml. They set data-pc-*
  // attributes and swap the preset stylesheet, so they must run once after the
  // template's pcoded.js has loaded.
  useEffect(() => {
    window.layout_change?.('light')
    window.layout_sidebar_change?.('false')
    window.change_box_container?.('false')
    window.layout_caption_change?.('true')
    window.layout_rtl_change?.('false')
    window.preset_change?.('preset-1')
    new (window.WOW ?? class {
      init() {}
    })({ animateClass: 'animate__animated' }).init()
  }, [])

  // "Menu hide/show on scroll", ported from the layout's inline script.
  useEffect(() => {
    let previousOffset = 0
    const onScroll = (): void => {
      const navbar = document.querySelector('.navbar')
      if (!navbar) return
      const offset = document.documentElement.scrollTop
      if (offset === 0) {
        navbar.classList.add('top-nav-collapse')
      } else if (offset > previousOffset) {
        navbar.classList.add('top-nav-collapse')
        navbar.classList.remove('default')
      } else {
        navbar.classList.add('default')
        navbar.classList.remove('top-nav-collapse')
      }
      previousOffset = offset
    }
    document.addEventListener('scroll', onScroll)
    return () => document.removeEventListener('scroll', onScroll)
  }, [])

  // feather.min.js swaps <i class="feather ..."> for inline SVG on load; React
  // renders those icons after that has already run, so re-run it per navigation.
  useEffect(() => {
    window.feather?.replace()
  })

  async function handleLogout(event: React.MouseEvent): Promise<void> {
    event.preventDefault()
    // The original was a GET link, which meant any page able to trigger a GET
    // could sign the user out. It is a POST now; the visible behaviour — sign
    // out, land on Home — is unchanged.
    await api.post<{ redirectUrl: string }>('/api/ecareer/account/logout')
    // A full reload rather than navigate(): it drops every cached query for the
    // signed-out user in one step, with no chance of a stale panel rendering.
    window.location.assign('/')
  }

  return (
    <>
      <header id="home">
        <nav className="navbar navbar-expand-md navbar-light default">
          <div className="container">
            <Link className="pc-navbar-brand" to="/">
              <img src="/lib/images/logo.png" alt="image" className="img-fluid" />
            </Link>

            <button
              className="navbar-toggler rounded"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarTogglerDemo01"
              aria-controls="navbarTogglerDemo01"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon" />
            </button>

            <div className="collapse navbar-collapse" id="navbarTogglerDemo01">
              <div className="flex-grow-1" />

              <ul className="navbar-nav mt-lg-0 mt-2 mb-2 mb-lg-0 align-items-center">
                <li className="nav-item px-2">
                  <Link className="nav-link" to="/">
                    Home
                  </Link>
                </li>
                <li className="nav-item px-2">
                  <Link className="nav-link" to="/Vacancy">
                    Vacancy
                  </Link>
                </li>
                <li className="dropdown px-1 me-2 mb-2 mb-md-0">
                  <a
                    className="nav-link"
                    data-bs-toggle="dropdown"
                    href="#"
                    role="button"
                    aria-haspopup="false"
                    aria-expanded="false"
                  >
                    Program
                  </a>
                  <div className="dropdown-menu dropdown-menu-end pc-h-dropdown">
                    <Link to="/Program/tldp" className="dropdown-item">
                      {' '}
                      TLDP{' '}
                    </Link>
                    <Link to="/Program/tbigin" className="dropdown-item">
                      TBIGIn
                    </Link>
                  </div>
                </li>
              </ul>

              <div className="flex-grow-1" />

              <ul className="navbar-nav mt-lg-0 mt-2 mb-2 mb-lg-0 align-items-center">
                {isAuthenticated ? (
                  <li className="dropdown px-1">
                    <a
                      className="nav-link dropdown-toggle d-flex align-items-center"
                      data-bs-toggle="dropdown"
                      href="#"
                      role="button"
                      aria-haspopup="false"
                      aria-expanded="false"
                    >
                      <div className="d-flex align-items-center">
                        <span className="d-none d-md-inline">{user?.FullName}</span>
                      </div>
                    </a>
                    <div
                      className="dropdown-menu dropdown-menu-end pc-h-dropdown"
                      style={{ minWidth: 250 }}
                    >
                      <div className="dropdown-header bg-light p-3">
                        <div className="d-flex align-items-center">
                          <div className="avtar avtar-m bg-primary rounded-circle me-3">
                            <i className="feather icon-user text-white" />
                          </div>
                          <div>
                            <h6 className="mb-0">{user?.FullName}</h6>
                          </div>
                        </div>
                      </div>

                      <div className="dropdown-divider" />

                      <Link
                        to="/Profile/FormProfile"
                        className="dropdown-item d-flex align-items-center py-2"
                      >
                        <i className="feather icon-user me-2" />
                        <span>Edit profile</span>
                      </Link>

                      <Link
                        to="/Profile/SavedVacancy"
                        className="dropdown-item d-flex align-items-center py-2"
                      >
                        <i className="feather icon-bookmark me-2" />
                        <span>Saved vacancy</span>
                      </Link>

                      <Link
                        to="/Profile/ApplicationHistory"
                        className="dropdown-item d-flex align-items-center py-2"
                      >
                        <i className="feather icon-clock me-2" />
                        <span>Application history</span>
                      </Link>

                      <div className="dropdown-divider" />

                      <Link
                        to="/Profile/ChangePassword"
                        className="dropdown-item d-flex align-items-center py-2"
                      >
                        <i className="feather icon-lock me-2" />
                        <span>Change password</span>
                      </Link>

                      {/* Inert in the original too — it linked to "#". */}
                      <a href="#" className="dropdown-item d-flex align-items-center py-2">
                        <i className="feather icon-shield me-2" />
                        <span>Two factor authentication</span>
                      </a>

                      <div className="dropdown-divider" />

                      <a
                        href="#"
                        onClick={(event) => void handleLogout(event)}
                        className="dropdown-item d-flex align-items-center py-2 text-danger"
                      >
                        <i className="feather icon-log-out me-2" />
                        <span>Log out</span>
                      </a>
                    </div>
                  </li>
                ) : (
                  <li className="nav-item px-1">
                    <a
                      className="nav-link"
                      href="/Account/Login"
                      onClick={(event) => {
                        event.preventDefault()
                        navigate('/Account/Login')
                      }}
                    >
                      <i className="feather icon-log-in" /> Login
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </nav>
      </header>

      {/*
        Resets the scroll position on navigation.

        Without it React Router leaves the window where the previous page was:
        clicking "Apply Now" from the bottom of the vacancy listing opened the
        application form already scrolled past its heading, so it was easy to
        miss that the page had changed at all. A full page load never behaved
        that way, which is why the Razor app did not need this.

        Back and forward still restore the position they left, which is what
        this does over a blunt scrollTo(0, 0).
      */}
      <ScrollRestoration />

      <div>
        <Outlet />
      </div>

      <Footer isAuthenticated={isAuthenticated} />
    </>
  )
}

/**
 * The footer's only conditional is the heading above the nav list
 * ("Navigation" vs "Get Started"); both branches listed the same three links in
 * the original, so they share one list here.
 */
function Footer({ isAuthenticated }: { isAuthenticated: boolean }): JSX.Element {
  return (
    <footer className="footer bg-white">
      <div className="footer-top">
        <div className="container">
          <div className="row gy-4">
            <div className="col-md-3">
              <img
                src="/lib/images/Logo-TBG.png"
                alt="image"
                className="img-fluid mb-3"
                style={{ maxWidth: '60%' }}
              />
              <p className="mb-3 text-muted">
                The Convergence Indonesia (TCI) 11TH Floor, Kawasan Rasuna Epicentrum, Jl. H.R Rasuna
                Said, Jakarta Selatan 12940, Indonesia.
              </p>
            </div>

            <div className="col-md-4">
              <div className="mb-4">
                <h5 className="text-primary mb-3">Anti-Bribery Commitment</h5>
                <p className="text-muted mb-0">
                  TBIG is committed to implementing the ISO 37001:2016 Anti-Bribery Management System
                  (ABMS) in the recruitment processes.
                </p>
              </div>
              <div>
                <h5 className="text-primary mb-3">Scams &amp; Fraud Warning!</h5>
                <p className="text-muted mb-0">
                  TBIG never ask for the exchange of money or credit card details in the recruitment
                  process. Please be aware of any suspicious emails.
                </p>
              </div>
            </div>

            <div className="col-md-2">
              <h5 className="text-primary mb-3">{isAuthenticated ? 'Navigation' : 'Get Started'}</h5>
              <ul className="list-unstyled footer-nav">
                <li className="mb-2">
                  <Link to="/" className="text-muted text-decoration-none">
                    <i className="feather icon-home me-2" />
                    Home
                  </Link>
                </li>
                <li className="mb-2">
                  <Link to="/Vacancy" className="text-muted text-decoration-none">
                    <i className="feather icon-briefcase me-2" />
                    Vacancy
                  </Link>
                </li>
                <li className="dropdown px-1 me-2 mb-2 mb-md-0">
                  <a
                    className="nav-link"
                    data-bs-toggle="dropdown"
                    href="#"
                    role="button"
                    aria-haspopup="false"
                    aria-expanded="false"
                  >
                    <i className="feather icon-users me-2" />
                    Program
                  </a>
                  <div className="dropdown-menu dropdown-menu-end pc-h-dropdown">
                    <Link to="/Program/tldp" className="dropdown-item">
                      {' '}
                      TLDP{' '}
                    </Link>
                    <Link to="/Program/tbigin" className="dropdown-item">
                      TBIGIn
                    </Link>
                  </div>
                </li>
              </ul>
            </div>

            <div className="col-md-3">
              <div className="bg-info text-white p-3 rounded mb-3">
                <h5 className="mb-0 text-white">Connect with us</h5>
              </div>

              <div className="row gy-3">
                <ContactItem icon="ph-phone" label="Phone" value="(+62) 21 2924 8900" />
                <ContactItem
                  icon="ph-envelope"
                  label="Mail"
                  value="Recruitment.tbg@tower-bersama.com"
                />
                <ContactItem icon="ph-instagram-logo" label="Instagram" value="tbigcareer" />
                <ContactItem icon="ph-linkedin-logo" label="LinkedIn" value="Tower Bersama Group" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <div className="row align-items-center">
            <div className="col my-1">
              {/*
                _Layout.cshtml:363 hard-coded "© 2024", so the footer has been
                showing a stale year since January 2025. Derived from the clock
                instead — LoginLayout already did this.
              */}
              <p className="m-0 text-muted">
                &copy; {new Date().getFullYear()} - PT Tower Bersama Group
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

function ContactItem({
  icon,
  label,
  value,
}: {
  icon: string
  label: string
  value: string
}): JSX.Element {
  return (
    <div className="col-6">
      <div className="d-flex align-items-center">
        <i className={`ph-duotone ${icon} text-primary me-2`} />
        <div className="min-w-0">
          <small className="text-muted">{label}</small>
          {/*
            "Recruitment.tbg@tower-bersama.com" has no break opportunity, so in
            a col-6 below ~1100px it pushed past the viewport and gave the whole
            page a horizontal scrollbar. Letting it wrap costs nothing and the
            address still reads normally.
          */}
          <div className="fw-medium" style={{ overflowWrap: 'anywhere' }}>
            {value}
          </div>
        </div>
      </div>
    </div>
  )
}
